/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902084103 — Стан і хуки лояльності */

import { create } from 'zustand';
import { IS_LOCAL_DEVELOPMENT } from '../../../shared/constants';
import { debug } from '../../../shared/lib/general';
import { Order } from '../../../shared/types';
import { paymentLoyalty, PaymentLoyaltyRequestDto } from '../../payment';
import { getLoyaltyDetails, getUserLoyaltyBalance } from '../api/loyalty.api';
import { GetLoyaltyBalanceRequestDto } from '../api/loyalty.api-types';
import {
    DEFAULT_DISABLED_LOYALTY_PROGRAM_BALANCE,
    DEFAULT_LOYALTY_BALANCE_FOR_PRIZE,
    DEFAULT_LOYALTY_PRODUCT_MIN_PRICE,
} from '../loyalty.constants';

export interface LoyaltyStateValues {
    currentBalance: number;
    pointsToShowLoyaltyBonus: number;
    pointsToGetLoyaltyReward: number;
    productMinPriceToGetBonusPoints: number;

    isLoading: boolean;
    isPaying: boolean;

    /** The mini-game is shown only where it was switched on for the company or
     *  the machine (Vadym, 25.08). Defaults to `false` everywhere: the field is
     *  not in `loyalty_config` yet, and the safe default is «nowhere», not «the
     *  whole fleet on release day». */
    isGameEnabled: boolean;

    // Persisted loyalty data
    loyalty_id: number;
    loyalty_config_id: number | null;
    loyalty_reward_id: number | null;
    loyalty_user_id: number;
}

export interface LoyaltyState extends LoyaltyStateValues {
    // Derived state
    isPrizeAvailable: () => boolean;
    isLoyaltyProgramActive: () => boolean;
    // Transient UI state — the loyalty banner hides once payment buttons show (SWRN-1747)
    arePaymentButtonsVisible: boolean;
    setPaymentButtonsVisible: (visible: boolean) => void;
    // Actions
    /** Applies a balance the server already confirmed, without a refetch: the
     *  game accrual (SWRN-1898) happens while the success screen stays mounted,
     *  so `LoyaltyProgress` has nothing that would reload it on its own. */
    setCurrentBalance: (balance: number) => void;
    fetchBalance: (dto: GetLoyaltyBalanceRequestDto) => Promise<void>;
    /** Re-reads only the balance; config and reward stay as `fetchBalance` left
     *  them. One request instead of three, for the success screen's poll (SWRN-2271). */
    refreshBalance: (dto: GetLoyaltyBalanceRequestDto) => Promise<void>;
    payWithLoyalty: (dto: PaymentLoyaltyRequestDto) => Promise<Order | null>;
}

const testInitialLoyaltyValues: LoyaltyStateValues = {
    currentBalance: 5,
    pointsToShowLoyaltyBonus: 5,
    pointsToGetLoyaltyReward: 5,
    productMinPriceToGetBonusPoints: DEFAULT_LOYALTY_PRODUCT_MIN_PRICE,

    isLoading: false,
    isPaying: false,
    isGameEnabled: true,

    loyalty_id: 1,
    loyalty_user_id: 0,
    loyalty_config_id: null,
    loyalty_reward_id: null,
}

const initialLoyaltyValues: LoyaltyStateValues = {
    currentBalance: DEFAULT_DISABLED_LOYALTY_PROGRAM_BALANCE,
    pointsToShowLoyaltyBonus: DEFAULT_LOYALTY_BALANCE_FOR_PRIZE,
    pointsToGetLoyaltyReward: DEFAULT_LOYALTY_BALANCE_FOR_PRIZE,
    productMinPriceToGetBonusPoints: DEFAULT_LOYALTY_PRODUCT_MIN_PRICE,

    isLoading: false,
    isPaying: false,
    isGameEnabled: false,

    loyalty_id: 0,
    loyalty_user_id: 0,
    loyalty_config_id: null,
    loyalty_reward_id: null,
}

const initialValues = IS_LOCAL_DEVELOPMENT ? testInitialLoyaltyValues : initialLoyaltyValues;

export const useLoyaltyStore = create<LoyaltyState>((set, get) => ({
    ...initialValues,
    arePaymentButtonsVisible: false,
    setPaymentButtonsVisible: (visible: boolean) =>
        set({ arePaymentButtonsVisible: visible }),
    setCurrentBalance: (balance: number) => set({ currentBalance: balance }),
    isLoyaltyProgramActive: () => {
        const { loyalty_id } = get();
        return Boolean(loyalty_id);
    },
    isPrizeAvailable: () => {
        const { loyalty_id, currentBalance, pointsToShowLoyaltyBonus } = get();

        if (!loyalty_id) {
            return false;
        }

        return currentBalance >= pointsToShowLoyaltyBonus;
    },
    fetchBalance: async (dto: GetLoyaltyBalanceRequestDto) => {
        set({ isLoading: true });
        try {
            const loyaltyDetails = await getLoyaltyDetails(dto);
            set(loyaltyDetails);
        } catch (error) {
            debug('fetchBalance failed', 'error', error);
        } finally {
            set({ isLoading: false });
        }
    },
    refreshBalance: async (dto: GetLoyaltyBalanceRequestDto) => {
        try {
            const { balance } = await getUserLoyaltyBalance(dto);
            set({ currentBalance: balance });
        } catch (error) {
            debug('refreshBalance failed', 'error', error);
        }
    },
    payWithLoyalty: async (
        dto: PaymentLoyaltyRequestDto,
    ): Promise<Order | null> => {
        set({ isPaying: true });
        try {
            const orderResponse = await paymentLoyalty(dto);
            set(() => ({ currentBalance: 0 }));
            return orderResponse;
        } catch (error) {
            debug('payWithLoyalty failed', 'error', error);
            return null;
        } finally {
            set({ isPaying: false });
        }
    },
}));
